import isEmpty from 'lodash/isEmpty';
import tinycolor from 'tinycolor2';

import {
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  IMAGE,
  LOCATION,
  MAINTENANCE,
  MAP,
  MIN_MAX_TYPE_SET,
  TEXT,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
  HISTORICAL_UTILIZATION_TYPES,
} from '../Constants/WidgetTypes';
import SessionStorageService from '../Services/SessionStorageService';
import TemplateDashboardService from '../Services/TemplateDashboardService';
import WidgetUtils from './WidgetUtils';

const NON_SIGNAL_TYPES = new Set([
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  LOCATION,
  MAINTENANCE,
  MAP,
  TEXT_ENGINE,
  TEXT_FUEL,
]);
export default class WidgetUpdateHelper {
  sessionStorageService = new SessionStorageService();
  templateDashboardService = new TemplateDashboardService();
  widgetUtils = new WidgetUtils();

  async updateGridLayout(currentWidgets, templateID, dashboard, cancelToken) {
    if (!Array.isArray(currentWidgets) || !currentWidgets.length) {
      throw new Error('No widgets to update');
    }

    const layout = this.sessionStorageService.getObjectItemByKey('gridLayout');
    if (!Array.isArray(layout) || !layout.length) {
      throw new Error('Error retrieving grid layout. Please try again.');
    }
    const layoutDictionary = layout.reduce((acc, l) => {
      acc[l.i] = l;
      return acc;
    }, {});

    const updates = currentWidgets.filter(({ updated }) => updated);
    if (!updates.length) return currentWidgets;

    const updatedWidgets = currentWidgets.map((widget) => {
      return this.formWidgetLayoutUpdateBody(layoutDictionary, widget);
    });
    const { _id: dashboardID, name } = dashboard || {};

    const { widgets } =
      await this.templateDashboardService.updateTemplateDashboard(
        templateID,
        dashboardID,
        { name, widgets: updatedWidgets },
        cancelToken
      );
    return widgets;
  }

  widgetHasBeenUpdated(layout, widget) {
    const { _id, col, row, sizeX, sizeY } = widget;
    const { w, h, x, y } = layout[_id] || {};

    const heightChanged = h !== sizeY;
    const widthChanged = w !== sizeX;
    const rowChanged = y !== row;
    const colChanged = x !== col;
    return heightChanged || widthChanged || rowChanged || colChanged;
  }

  formWidgetLayoutUpdateBody(layout, { updated, ...widget }) {
    const { h, w, x, y } = layout[widget._id] || {};
    return {
      ...widget,
      sizeY: h,
      sizeX: w,
      col: x,
      row: y,
    };
  }

  _formTimeSeriesWidgetUpdate(widgetSignals, isImageOverlay) {
    const update = widgetSignals
      .map(({ datapoint, signal, x, y }) => {
        if (!datapoint.value) {
          throw new Error('Smart Tile must have datapoint selected!');
        } else if (!signal.label) {
          throw new Error('Smart Tile must have signal selected');
        }

        const conditionalImageOverlyProps = isImageOverlay ? { x, y } : {};
        let conditionalType = {};
        let datapointID = datapoint.value;
        if (datapoint.value === 'outputSignal') {
          conditionalType = { type: datapoint.value };
          if (!signal?.value?._id) {
            throw new Error('Smart Tile must have signal selected');
          }
          datapointID = signal.value._id;
        }
        return {
          ...conditionalImageOverlyProps,
          ...conditionalType,
          datapointID,
          key: signal.label,
        };
      })
      .filter((u) => !!u);
    if (isEmpty(update)) {
      throw new Error('Signal must be selected for smart tile.');
    }
    return update;
  }

  setCommandSettings(widgetSignals = []) {
    return widgetSignals.map((c) => {
      const { color, command: { value } = {}, friendlyName } = c || {};
      if (!color || !value || !friendlyName) {
        throw new Error(
          'Each row must have a command, friendly name and color selected.'
        );
      }
      return { color, commandID: value, friendlyName };
    });
  }

  buildUpdateRequestBody(
    updates,
    { _id, image, type, settings },
    { datapoints },
    imageCoordinatesMap
  ) {
    const {
      displayTextAsGrid,
      selectedAggregation,
      selectedInstances,
      showTimeSinceLastUpdated,
      widgetName,
      widgetSignals,
    } = updates;
    if (!widgetSignals.length && !NON_SIGNAL_TYPES.has(type)) {
      throw new Error('Signal must be selected for smart tile.');
    }
    if (type === MAINTENANCE && !selectedInstances?.length) {
      throw new Error('Reminder must be selected for smart tile.');
    }

    const conditionalImageSetting = type === IMAGE ? { image } : {};
    const conditionalMaintenanceSetting =
      type === MAINTENANCE
        ? {
            maintenance: selectedInstances.map((opt) => ({
              reminder_id: opt.value,
              reminder_name: opt.label,
            })),
          }
        : {};
    const conditionalTextSettings =
      type === TEXT ? { displayTextAsGrid, showTimeSinceLastUpdated } : {};
    const conditionalUtilizationSettings = HISTORICAL_UTILIZATION_TYPES.has(
      type
    )
      ? { aggregation: selectedAggregation }
      : {};

    const body = {
      _id,
      settings: {
        ...conditionalImageSetting,
        ...conditionalMaintenanceSetting,
        ...conditionalTextSettings,
        ...conditionalUtilizationSettings,
        title: widgetName,
      },
      type,
    };
    if (MIN_MAX_TYPE_SET.has(type)) {
      const yMax = Number.parseFloat(updates.yMax),
        yMin = Number.parseFloat(updates.yMin);
      if (Number.isFinite(yMax)) body.settings.yMax = yMax;
      if (Number.isFinite(yMin)) body.settings.yMin = yMin;
    }

    const { settingsSignalArrayNameMap } = this.widgetUtils;
    const isImageOverlay = type === IMAGE;
    switch (true) {
      case type === COMMAND:
        body.settings = {
          ...body.settings,
          commands: this.setCommandSettings(widgetSignals),
        };
        break;
      case settingsSignalArrayNameMap.has(type):
        body.settings[settingsSignalArrayNameMap.get(type)] =
          this._formTimeSeriesWidgetUpdate(
            isImageOverlay ? [...imageCoordinatesMap.values()] : widgetSignals,
            isImageOverlay
          );
        break;
      case type === GAUGE: {
        const [
          {
            datapoint: { value: selectedDP },
            maxValue,
            minValue,
            signal,
          },
          {
            datapoint: datapoint2,
            maxValue: maxValue2,
            minValue: minValue2,
            signal: signal2,
          } = {},
        ] = widgetSignals;
        if (isEmpty(selectedDP) || isEmpty(signal)) {
          throw new Error('Signal must be selected for smart tile.');
        }

        const max = Number.parseFloat(maxValue);
        const min = Number.parseFloat(minValue);
        if (Number.isNaN(max) || Number.isNaN(min)) {
          throw new Error('Min and Max must be configured.');
        }
        if (max <= min) throw new Error('Max value must be greater than min.');

        if (selectedDP === 'outputSignal') {
          body.settings.type = selectedDP;
          body.settings.datapoint = signal.value;
          body.settings.datapointID = signal.value?._id;
        } else {
          body.settings.datapoint = datapoints.find(
            ({ _id }) => _id === selectedDP
          );
          body.settings.datapointID = selectedDP;
        }
        body.settings.key = signal.label;
        body.settings.max = max;
        body.settings.min = min;
        if (datapoint2?.value) {
          const max2 = Number.parseFloat(maxValue2);
          const min2 = Number.parseFloat(minValue2);
          if (Number.isNaN(max2) || Number.isNaN(min2)) {
            throw new Error('Min and Max must be configured.');
          }
          if (max2 <= min2) {
            throw new Error('Max value must be greater than min.');
          }

          if (datapoint2?.value === 'outputSignal') {
            body.settings.secondGauge = {
              datapoint: signal2?.value,
              datapointID: signal2?.value?._id,
              key: signal2?.label,
              max: max2,
              min: min2,
              type: datapoint2.value,
            };
          } else {
            body.settings.secondGauge = {
              datapoint: datapoints.find(({ _id }) => _id === datapoint2.value),
              datapointID: datapoint2.value,
              key: signal2?.label,
              max: max2,
              min: min2,
            };
          }
        }
        break;
      }
      case type === GAUGE_RANGE: {
        const [
          {
            datapoint: { value: selectedDP },
            ranges,
            signal,
          },
          { datapoint: datapoint2, ranges: ranges2, signal: signal2 } = {},
        ] = widgetSignals;
        if (isEmpty(selectedDP) || isEmpty(signal)) {
          throw new Error('Signal must be selected for smart tile.');
        }

        const firstWidgetRanges = ranges.map(
          ({ color, label, maxValue, minValue }) => {
            validateWidgetRanges({ color, label, maxValue, minValue });
            const max = Number.parseFloat(maxValue);
            const min = Number.parseFloat(minValue);
            return { color, label, max, min };
          }
        );

        if (selectedDP === 'outputSignal') {
          body.settings.type = selectedDP;
          body.settings.datapoint = signal.value;
          body.settings.datapointID = signal.value?._id;
        } else {
          body.settings.datapoint = datapoints.find(
            ({ _id }) => _id === selectedDP
          );
          body.settings.datapointID = selectedDP;
        }

        body.settings.key = signal.label;
        body.settings.ranges = firstWidgetRanges;
        if (datapoint2?.value) {
          const secondWidgetRanges = ranges2.map(
            ({ color, label, maxValue, minValue }) => {
              validateWidgetRanges({ color, label, maxValue, minValue });
              const max = Number.parseFloat(maxValue);
              const min = Number.parseFloat(minValue);
              return { color, label, max, min };
            }
          );
          if (datapoint2?.value === 'outputSignal') {
            body.settings.secondGauge = {
              datapoint: signal2?.value,
              datapointID: signal2?.value?._id,
              key: signal2.label,
              ranges: secondWidgetRanges,
            };
          } else {
            body.settings.secondGauge = {
              datapoint: datapoints.find(({ _id }) => _id === datapoint2.value),
              datapointID: datapoint2.value,
              key: signal2.label,
              ranges: secondWidgetRanges,
            };
          }
        }
        break;
      }
      case [LOCATION, MAP].includes(type):
        body.settings = { ...settings, ...body.settings };
        break;
      case type === TEXT_STATIC: {
        const [{ links: inputLinks, value: staticText = '' } = {}] =
          widgetSignals;
        if (!staticText || staticText.length > 512) {
          throw new Error('Text required. Maximum characters allowed is 512');
        }

        const links = inputLinks?.reduce?.(
          (acc, l) => (l?.display && l?.url ? acc.concat(l) : acc),
          []
        );
        body.settings.links = links;
        body.settings.staticText = staticText;
        break;
      }
      case type === TEXT_TAGS:
        body.settings.tags = widgetSignals
          .map(({ value }) => value)
          .filter((t) => !!t);
        break;
      default:
        break;
    }

    return body;
  }
}

function validateWidgetRanges({ color, label, maxValue, minValue }) {
  if (!tinycolor(color).isValid()) {
    throw new Error('Valid hex color must be configured for each range.');
  }
  if (!label) throw new Error('Label must be configured for each range.');
  if (Number.isNaN(Number.parseFloat(maxValue))) {
    throw new Error(
      'Max value for ranges must be a valid floating point number.'
    );
  }
  if (Number.isNaN(Number.parseFloat(minValue))) {
    throw new Error(
      'Min value for ranges must be a valid floating point number.'
    );
  }
  if (Number.parseFloat(maxValue) < Number.parseFloat(minValue)) {
    throw new Error('Max value must be greater than Min value for each range.');
  }
  if (Number.parseFloat(minValue) > Number.parseFloat(maxValue)) {
    throw new Error('Min value must be less than Max value for each range.');
  }
}
